/**
 * React Component Registry Service
 *
 * Service for looking up and retrieving React page components
 * Provides abstraction over the component registry
 */

import {
  PAGE_COMPONENT_REGISTRY,
  getAllComponentIds,
  isComponentRegistered,
  type ComponentRegistryEntry,
  type PageComponentProps,
} from './react-component-registry.constants';
import type { ComponentType } from 'react';

/**
 * React Component Registry Service
 */
class ReactComponentRegistryService {
  /**
   * Get a React component by componentId
   *
   * @param componentId - The component identifier
   * @returns React component or null if not found
   */
  getComponent(componentId: string): ComponentType<PageComponentProps> | null {
    const entry = PAGE_COMPONENT_REGISTRY[componentId];

    if (!entry) {
      console.error(
        `[ReactComponentRegistry] Component not found: ${componentId}`,
        '\nAvailable components:',
        this.getRegisteredComponentIds()
      );
      return null;
    }

    console.log(`[ReactComponentRegistry] Found component: ${entry.displayName} (${componentId})`);
    return entry.component;
  }

  /**
   * Get component registry entry with metadata
   *
   * @param componentId - The component identifier
   * @returns Registry entry or null if not found
   */
  getComponentEntry(componentId: string): ComponentRegistryEntry | null {
    return PAGE_COMPONENT_REGISTRY[componentId] || null;
  }

  /**
   * Check if a component is registered
   *
   * @param componentId - The component identifier
   * @returns true if component is registered
   */
  isRegistered(componentId: string): boolean {
    return isComponentRegistered(componentId);
  }

  /**
   * Get all registered component IDs
   *
   * @returns Array of component IDs
   */
  getRegisteredComponentIds(): string[] {
    return getAllComponentIds();
  }

  /**
   * Get all registry entries
   *
   * @returns Record of all component registry entries
   */
  getAllEntries(): Record<string, ComponentRegistryEntry> {
    return PAGE_COMPONENT_REGISTRY;
  }

  /**
   * Log registry information for debugging
   */
  logRegistry(): void {
    console.group('[ReactComponentRegistry] Registered Components');
    Object.entries(PAGE_COMPONENT_REGISTRY).forEach(([id, entry]) => {
      console.log(`- ${id}: ${entry.displayName}${entry.description ? ` (${entry.description})` : ''}`);
    });
    console.groupEnd();
  }
}

// Export singleton instance
export const reactComponentRegistry = new ReactComponentRegistryService();

// Export class for testing
export { ReactComponentRegistryService };
